import { RATE_SEASON_LABELS_FR, type RateSeason } from '@lemontaigu/pms-core';
import {
  CLOSED_COLOR,
  SEASON_COLORS,
  SEASON_SHORT,
  daysInclusive,
  dayOffset,
  fiscalYearBounds,
  type SeasonCal,
} from './seasonBands.js';

// « Saisons » timeline — the whole fiscal year drawn as one horizontal bar, each
// period sized by its day count, closures hatched across it. Read-only: editing
// stays in the period table below it.

const MONTHS_FR = ['Déc', 'Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov'];

/** The month starts across the fiscal year, Dec → Nov, as ISO dates. */
function monthStarts(year: number): { iso: string; label: string }[] {
  const out: { iso: string; label: string }[] = [];
  let y = year - 1;
  let m = 12;
  for (let i = 0; i < 12; i++) {
    out.push({ iso: `${y}-${String(m).padStart(2, '0')}-01`, label: MONTHS_FR[i]! });
    m += 1;
    if (m === 13) {
      m = 1;
      y += 1;
    }
  }
  return out;
}

/** « 19/12 » — the table below carries the full descriptions and years. */
function shortDay(iso: string): string {
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
}

export function SeasonTimeline({ calendar }: { calendar: SeasonCal | null }) {
  if (!calendar) return null;
  const { from, to } = fiscalYearBounds(calendar.year);
  const total = daysInclusive(from, to);
  const pct = (days: number) => `${(days / total) * 100}%`;
  const periods = [...calendar.periods].sort((a, b) => (a.from < b.from ? -1 : 1));
  const used = [...new Set(periods.map((p) => p.season))] as RateSeason[];

  return (
    <section className="pms-season-tl" aria-label={`Saisons ${calendar.year}`}>
      <div className="pms-season-tl-months" aria-hidden="true">
        {monthStarts(calendar.year).map((m) => (
          <span key={m.iso} style={{ left: pct(dayOffset(from, m.iso)) }}>
            {m.label}
          </span>
        ))}
      </div>

      <div className="pms-season-tl-bar">
        {periods.map((p) => {
          const days = daysInclusive(p.from, p.to);
          const left = pct(dayOffset(from, p.from));
          const wide = days / total > 0.07;
          const tip = `${RATE_SEASON_LABELS_FR[p.season]} — ${p.description} (${p.from} → ${p.to})`;
          return (
            <div
              key={p.id}
              className={`pms-season-seg is-${p.season}`}
              style={{ left, width: pct(days), background: SEASON_COLORS[p.season] }}
              title={tip}
            >
              {wide ? (
                <span className="pms-season-seg-text">
                  <strong>{RATE_SEASON_LABELS_FR[p.season]}</strong>
                  <span>
                    {shortDay(p.from)} → {shortDay(p.to)}
                  </span>
                </span>
              ) : (
                <span className="pms-season-seg-short">{SEASON_SHORT[p.season]}</span>
              )}
            </div>
          );
        })}

        {calendar.closures.map((c) => (
          <div
            key={`${c.from}-${c.to}`}
            className="pms-season-closure"
            style={{
              left: pct(dayOffset(from, c.from)),
              width: pct(daysInclusive(c.from, c.to)),
              background: CLOSED_COLOR,
            }}
            title={`Fermé (${c.from} → ${c.to})${c.reason ? ` — ${c.reason}` : ''}`}
          />
        ))}
      </div>

      <ul className="pms-season-legend">
        {used.map((s) => (
          <li key={s}>
            <span className="pms-season-swatch" style={{ background: SEASON_COLORS[s] }} aria-hidden="true" />
            {RATE_SEASON_LABELS_FR[s]}
          </li>
        ))}
        {calendar.closures.length > 0 && (
          <li>
            <span className="pms-season-swatch is-closed" style={{ background: CLOSED_COLOR }} aria-hidden="true" />
            Fermé
          </li>
        )}
      </ul>
    </section>
  );
}
